// src/app/api/customFunnelApi.ts
import { baseApi } from "./baseApi";

// ─── Lead types ───────────────────────────────────────────────────────────────

export type CustomFunnelLead = {
  _id?: string;
  firstName: string;
  lastName?: string;
  email: string;
  phoneNo?: string;
  funnelName: string;
  source?: string;
  status?: "pre_registered" | "waiting" | "notified" | "enrolled";
  metrics?: {
    openedEmails?: number;
    clickedLinks?: number;
    funnelStepsCompleted?: number;
    lastActivityAt?: string | null;
  };
  consent?: {
    email?: boolean;
    sms?: boolean;
    consentGivenAt?: string | null;
  };
  meta?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
};

export type CreateCustomFunnelRequest = Partial<
  Pick<CustomFunnelLead, "firstName" | "lastName" | "email" | "phoneNo" | "funnelName" | "source" | "meta">
>;

export type UpdateCustomFunnelRequest = Partial<
  Pick<CustomFunnelLead, "firstName" | "lastName" | "email" | "phoneNo" | "status" | "consent" | "meta" | "funnelName" | "source">
>;

// ─── Webinar / Demo types ─────────────────────────────────────────────────────

/**
 * POST /api/custom-funnel/webinar-register
 *
 * Registers a user for the free sales-system demo webinar.
 * Returns a `uniqueId` used to build the access URL:
 *   baseUrl/sales-funnel/webinar/:uniqueId
 */
export type WebinarRegisterRequest = {
  firstName: string;
  email: string;
  /** Optional WhatsApp number */
  whatsapp?: string;
};

export type WebinarRegisterResponse = {
  success: boolean;
  /** UUID — build the redirect URL as: /sales-funnel/webinar/:uniqueId */
  uniqueId?: string;
  leadId?: string;
  message?: string;
};

/**
 * GET /api/custom-funnel/webinar/:uniqueId
 *
 * Validates a webinar access token. Called by DemoViewPage on mount.
 * Returns the registrant's name/email if the token is valid.
 */
export type WebinarAccessResponse = {
  valid: boolean;
  firstName?: string;
  email?: string;
  message?: string;
};

// ─── Payment types ────────────────────────────────────────────────────────────

export type CustomFunnelProduct = {
  id: string;
  name: string;
  price: string;
  priceNumeric: number;
};

export type CustomFunnelFeature = {
  title: string;
  description?: string;
  value?: number;
};

export type CustomFunnelTotals = {
  featuresTotal: number;
  productPrice: number;
};

export type InitializeCustomFunnelRequest = {
  firstName: string;
  lastName?: string;
  email: string;
  phoneNo?: string;
  funnelName: string;
  product: CustomFunnelProduct;
  paymentPlan?: "full" | "split";
  features?: CustomFunnelFeature[];
  totals?: CustomFunnelTotals;
  metadata?: Record<string, any>;
};

export type InitializeCustomFunnelResponse = {
  success: boolean;
  authorization_url?: string;
  reference?: string;
  orderId?: string;
  leadId?: string;
  whatsappRedirectUrl?: string;
  funnelName?: string;
  message?: string;
};

export type VerifyCustomFunnelResponse = {
  success: boolean;
  verified?: boolean;
  data?: any;
  orderId?: string;
  whatsappRedirectUrl?: string;
  message?: string;
};

export type InitializeLaunchKitNaijaRequest = Omit<InitializeCustomFunnelRequest, "funnelName"> & {
  funnelName: "launch_kit_naija";
};

// ─── API slice ────────────────────────────────────────────────────────────────

export const customFunnelApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── CRUD ──────────────────────────────────────────────────────────────────

    createCustomFunnelLead: builder.mutation<CustomFunnelLead, CreateCustomFunnelRequest>({
      query: (data) => ({ url: "/custom-funnel", method: "POST", body: data }),
      invalidatesTags: [{ type: "CustomFunnelLead", id: "LIST" }],
    }),

    getCustomFunnelLeads: builder.query<CustomFunnelLead[], void>({
      query: () => ({ url: "/custom-funnel", method: "GET" }),
      providesTags: (result) =>
        result
          ? [...result.map((r) => ({ type: "CustomFunnelLead" as const, id: r._id })), { type: "CustomFunnelLead" as const, id: "LIST" }]
          : [{ type: "CustomFunnelLead" as const, id: "LIST" }],
    }),

    getCustomFunnelLead: builder.query<CustomFunnelLead, string>({
      query: (id) => ({ url: `/custom-funnel/${id}`, method: "GET" }),
      providesTags: (result, error, id) => [{ type: "CustomFunnelLead", id }],
    }),

    updateCustomFunnelLead: builder.mutation<CustomFunnelLead, { id: string; data: UpdateCustomFunnelRequest }>({
      query: ({ id, data }) => ({ url: `/custom-funnel/${id}`, method: "PUT", body: data }),
      invalidatesTags: (result, error, { id }) => [
        { type: "CustomFunnelLead", id },
        { type: "CustomFunnelLead", id: "LIST" },
      ],
    }),

    deleteCustomFunnelLead: builder.mutation<CustomFunnelLead, string>({
      query: (id) => ({ url: `/custom-funnel/${id}`, method: "DELETE" }),
      invalidatesTags: (result, error, id) => [
        { type: "CustomFunnelLead", id },
        { type: "CustomFunnelLead", id: "LIST" },
      ],
    }),

    // ── Webinar / Demo ────────────────────────────────────────────────────────

    /**
     * registerWebinar
     *
     * POST /api/custom-funnel/webinar-register
     *
     * Usage in DemoRequestPage:
     *   const [registerWebinar, { isLoading }] = useRegisterWebinarMutation();
     *   const res = await registerWebinar({ firstName, email, whatsapp }).unwrap();
     *   navigate(`/sales-funnel/webinar/${res.uniqueId}`);
     */
    registerWebinar: builder.mutation<WebinarRegisterResponse, WebinarRegisterRequest>({
      query: (payload) => ({
        url:    "/custom-funnel/webinar-register",
        method: "POST",
        body:   payload,
      }),
    }),

    /**
     * verifyWebinarAccess
     *
     * GET /api/custom-funnel/webinar/:uniqueId
     *
     * Usage in DemoViewPage:
     *   const { data, isLoading, isError } = useVerifyWebinarAccessQuery(uniqueId);
     *   if (!data?.valid) → redirect to /sales-funnel/sales/demo-request
     */
    verifyWebinarAccess: builder.query<WebinarAccessResponse, string>({
      query: (uniqueId) => ({
        url:    `/custom-funnel/webinar/${encodeURIComponent(uniqueId)}`,
        method: "GET",
      }),
    }),

    // ── Payment ───────────────────────────────────────────────────────────────

    initializeCustomFunnel: builder.mutation<InitializeCustomFunnelResponse, InitializeCustomFunnelRequest>({
      query: (payload) => ({ url: "/custom-funnel/initialize", method: "POST", body: payload }),
    }),

    verifyCustomFunnelPayment: builder.query<VerifyCustomFunnelResponse, string>({
      query: (reference) => ({ url: `/custom-funnel/verify/${encodeURIComponent(reference)}`, method: "GET" }),
    }),
  }),
  overrideExisting: false,
});

// ─── Hooks ────────────────────────────────────────────────────────────────────

export const {
  useCreateCustomFunnelLeadMutation,
  useGetCustomFunnelLeadsQuery,
  useGetCustomFunnelLeadQuery,
  useUpdateCustomFunnelLeadMutation,
  useDeleteCustomFunnelLeadMutation,
  useRegisterWebinarMutation,
  useVerifyWebinarAccessQuery,
  useInitializeCustomFunnelMutation,
  useVerifyCustomFunnelPaymentQuery,
} = customFunnelApi;

// ─── useInitializeLaunchKitNaijaMutation ─────────────────────────────────────

export function useInitializeLaunchKitNaijaMutation() {
  const [mutate, state] = useInitializeCustomFunnelMutation();
  const initializeLaunchKitNaija = (payload: Omit<InitializeLaunchKitNaijaRequest, "funnelName">) =>
    mutate({ ...payload, funnelName: "launch_kit_naija" });
  return [initializeLaunchKitNaija, state] as const;
}